import React, { useState, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';
import { FaTimes, FaCube } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import { getBadgeTailwindClasses } from '../../styles/designSystem';

interface RackUnitInterface {
  id?: string;
  position: number;
  device_model: string;
  power_usage: number;
}

interface RackInterface {
  id?: string;
  name?: string;
  number: string;
  total_units: number;
  max_power?: number;
  units?: RackUnitInterface[];
}

interface RowInterface {
  id?: string;
  name: string;
  racks?: RackInterface[];
}

interface RoomInterface {
  id?: string;
  name: string;
  rows?: RowInterface[];
}

interface Room3DViewProps {
  room: RoomInterface;
  projectId: string;
  siteId: string;
  floorId: string;
  roomId: string;
  isOpen: boolean;
  onClose: () => void;
}

// Constants for 3D visualization
const RACK_WIDTH = 0.8;
const RACK_HEIGHT = 2.0;
const RACK_DEPTH = 0.6;
const UNIT_HEIGHT = RACK_HEIGHT / 45; // Assuming 45U standard
const RACK_SPACING = 1.2;
const ROW_SPACING = 3.0;
const AIR_FLOW_HEIGHT = 1.5;
const AIR_FLOW_WIDTH = 18;

// Individual Rack Component
const Rack3D: React.FC<{
  rack: RackInterface;
  position: [number, number, number];
  onClick: (rackId: string) => void;
}> = ({ rack, position, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);

  // Removed rotation animation for better UX

  const getUnitColor = (unit: RackUnitInterface | null) => {
    if (!unit) return '#aaa'; // Gray for empty
    if (unit.device_model) return '#4ade80'; // Green for occupied
    return '#fbbf24'; // Amber for reserved
  };

  return (
    <group position={position}>
      {/* Rack frame */}
      <mesh
        ref={meshRef}
        onClick={() => rack.id && onClick(rack.id)}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        scale={hovered ? 1.05 : 1}
      >
        <boxGeometry args={[RACK_WIDTH, RACK_HEIGHT, RACK_DEPTH]} />
        <meshStandardMaterial 
          color={hovered ? '#4ade80' : '#888'} 
          opacity={hovered ? 0.8 : 1}
          transparent
        />
      </mesh>

      {/* Rack label */}
      <Text
        position={[0, RACK_HEIGHT / 2 + 0.1, 0]}
        fontSize={0.1}
        color="#171717"
        anchorX="center"
        anchorY="middle"
      >
        {rack.name || rack.number}
      </Text>

      {/* Units */}
      {rack.units?.map((unit) => (
        <mesh
          key={unit.id}
          position={[
            0,
            -RACK_HEIGHT / 2 + (unit.position - 1) * UNIT_HEIGHT + UNIT_HEIGHT / 2,
            RACK_DEPTH / 2 + 0.01
          ]}
        >
          <boxGeometry args={[RACK_WIDTH * 0.9, UNIT_HEIGHT * 0.8, 0.02]} />
          <meshStandardMaterial color={getUnitColor(unit)} />
        </mesh>
      ))}

      {/* Power indicator */}
      {rack.units?.some(unit => unit.power_usage && unit.power_usage > 0) && (
        <mesh position={[RACK_WIDTH * 0.4, 0, 0]}>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshStandardMaterial color="#ef4444" />
        </mesh>
      )}
    </group>
  );
};

// Air Flow Component
const AirFlow3D: React.FC<{
  isHotFlow: boolean;
  position: [number, number, number];
}> = ({ isHotFlow, position }) => {
  const [time, setTime] = useState(0);
  
  // Animate the air flow
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTime(prev => prev + 0.1);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const flowColor = isHotFlow ? "#ef4444" : "#3b82f6";
  const flowOpacity = 0.3 + 0.2 * Math.sin(time * 2); // Pulsing effect

  return (
    <group position={position}>
      {/* Rainbow air flow columns (like in the reference image) */}
      {Array.from({ length: 5 }).map((_, i) => {
        const colors = ['#3b82f6', '#10b981', '#fbbf24', '#f97316', '#ef4444'];
        const columnColor = colors[i];
        const columnOpacity = 0.3 + 0.2 * Math.sin(time + i);
        
        return (
          <mesh
            key={i}
            position={[
              (i - 2) * 3,
              0,
              0
            ]}
          >
            <cylinderGeometry args={[0.05, 0.05, AIR_FLOW_HEIGHT, 6]} />
            <meshStandardMaterial 
              color={columnColor}
              opacity={columnOpacity}
              transparent
            />
          </mesh>
        );
      })}
      
      {/* Main air flow stream */}
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.1, 0.1, AIR_FLOW_WIDTH, 8]} />
        <meshStandardMaterial 
          color={flowColor}
          opacity={flowOpacity * 0.5}
          transparent
        />
      </mesh>
      
      {/* Air flow particles */}
      {Array.from({ length: 8 }).map((_, i) => (
        <mesh
          key={i}
          position={[
            (i - 4) * 2 + Math.sin(time + i) * 0.5,
            Math.sin(time * 2 + i) * 0.3,
            0
          ]}
        >
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial 
            color={flowColor}
            opacity={0.6}
            transparent
          />
        </mesh>
      ))}
      
      {/* Air flow direction indicator */}
      <mesh position={[AIR_FLOW_WIDTH / 2 - 0.5, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <coneGeometry args={[0.05, 0.2, 6]} />
        <meshStandardMaterial color={flowColor} />
      </mesh>
    </group>
  );
};

// Row Component
const Row3D: React.FC<{
  row: RowInterface;
  rowIndex: number;
  onRackClick: (rackId: string) => void;
}> = ({ row, rowIndex, onRackClick }) => {
  const rowZ = rowIndex * ROW_SPACING;
  const isHotAisle = rowIndex % 2 === 0; // Even rows are hot aisles

  return (
    <group position={[0, 0, rowZ]}>
      {/* Hot/Cold Aisle Label */}
      <Text
        position={[-4, RACK_HEIGHT + 0.5, 0]}
        fontSize={0.2}
        color={isHotAisle ? "#ef4444" : "#3b82f6"}
        anchorX="left"
        anchorY="middle"
      >
        {isHotAisle ? "HOT AISLE" : "COLD AISLE"}
      </Text>

      {/* Row label */}
      <Text
        position={[-4, RACK_HEIGHT / 2, 0]}
        fontSize={0.15}
        color="#444"
        anchorX="left"
        anchorY="middle"
      >
        {row.name}
      </Text>

      {/* Aisle floor indicator */}
      <mesh position={[0, -0.9, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, ROW_SPACING * 0.8]} />
        <meshStandardMaterial 
          color={isHotAisle ? "#fef2f2" : "#f0f9ff"} 
          opacity={0.4}
          transparent
        />
      </mesh>

      {/* Racks in this row */}
      {row.racks?.map((rack, rackIndex) => (
        <Rack3D
          key={rack.id}
          rack={rack}
          position={[rackIndex * RACK_SPACING, RACK_HEIGHT / 2, 0]}
          onClick={onRackClick}
        />
      ))}
    </group>
  );
};

// Main 3D Scene Component
const Room3DScene: React.FC<{
  room: RoomInterface;
  onRackClick: (rackId: string) => void;
}> = ({ room, onRackClick }) => {
  const totalDepth = (room.rows?.length || 0) * ROW_SPACING;
  
  // Create heat map texture
  const createHeatMapTexture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    gradient.addColorStop(0, '#3b82f6');    // Blue (cold)
    gradient.addColorStop(0.25, '#10b981'); // Green
    gradient.addColorStop(0.5, '#fbbf24');  // Yellow
    gradient.addColorStop(0.75, '#f97316'); // Orange
    gradient.addColorStop(1, '#ef4444');    // Red (hot)
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 2); // Repeat the texture to cover the floor
    
    return texture;
  };
  
  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={0.8} />
      <pointLight position={[-10, -10, -5]} intensity={0.3} />

      {/* Room floor with heat map */}
      <mesh position={[0, -1, totalDepth / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, totalDepth + 2]} />
        <meshStandardMaterial map={createHeatMapTexture()} />
      </mesh>

      {/* Room walls */}
      <mesh position={[-10, 2, totalDepth / 2]}>
        <boxGeometry args={[0.2, 6, totalDepth + 2]} />
        <meshStandardMaterial color="#e2e8f0" />
      </mesh>
      <mesh position={[10, 2, totalDepth / 2]}>
        <boxGeometry args={[0.2, 6, totalDepth + 2]} />
        <meshStandardMaterial color="#e2e8f0" />
      </mesh>

      {/* Rows */}
      {room.rows?.map((row, index) => (
        <Row3D
          key={row.id}
          row={row}
          rowIndex={index}
          onRackClick={onRackClick}
        />
      ))}

      {/* Air flows between rows */}
      {room.rows && room.rows.length > 1 && room.rows.slice(0, -1).map((_, index) => {
        const isHotFlow = index % 2 === 0; // Hot flow from hot aisle to cold aisle
        const flowZ = (index + 0.5) * ROW_SPACING;
        return (
          <AirFlow3D
            key={`airflow-${index}`}
            isHotFlow={isHotFlow}
            position={[0, AIR_FLOW_HEIGHT / 2, flowZ]}
          />
        );
      })}

      {/* Room label */}
      <Text
        position={[0, 3, totalDepth + 1]}
        fontSize={0.3}
        color="#171717"
        anchorX="center"
        anchorY="middle"
      >
        {room.name}
      </Text>
    </>
  );
};

export const Room3DView: React.FC<Room3DViewProps> = ({
  room,
  projectId,
  siteId,
  floorId,
  roomId,
  isOpen,
  onClose
}) => {
  const navigate = useNavigate();

  const handleRackClick = (rackId: string) => {
    // Find the row that contains this rack
    const row = room.rows?.find(r => r.racks?.some(rack => rack.id === rackId));
    if (row) {
      const rackUrl = `/projects/${projectId}/sites/${siteId}/floors/${floorId}/rooms/${roomId}/rows/${row.id}/racks/${rackId}`;
      navigate(rackUrl);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
          <div className="flex items-center space-x-3">
            <FaCube className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-[#171717]" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
              3D View - {room.name} <span className={getBadgeTailwindClasses('dev')}>dev</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[#aaa] hover:text-[#555] hover:bg-[#fafafa] rounded-md transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* 3D Canvas */}
        <div className="flex-1 relative">
          <Canvas
            camera={{ position: [15, 8, 15], fov: 45 }}
            style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
          >
            <Room3DScene room={room} onRackClick={handleRackClick} />
            <OrbitControls 
              enablePan={true}
              enableZoom={true}
              enableRotate={true}
              minDistance={5}
              maxDistance={50}
            />
          </Canvas>
        </div>

        {/* Legend */}
        <div className="p-4 bg-[#fafafa] border-t border-[#eaeaea]">
          <div className="flex items-center justify-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-[#bbb] rounded"></div>
              <span>Empty Unit</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-400 rounded"></div>
              <span>Occupied Unit</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-amber-400 rounded"></div>
              <span>Reserved Unit</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span>Power Indicator</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-100 rounded"></div>
              <span>Hot Aisle</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-sky-100 rounded"></div>
              <span>Cold Aisle</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-500 rounded-full opacity-60"></div>
              <span>Hot Air Flow</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-[#555] rounded-full opacity-60"></div>
              <span>Cold Air Flow</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-2 bg-gradient-to-r from-blue-500 via-green-500 via-yellow-500 via-orange-500 to-red-500 rounded"></div>
              <span>Heat Map</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 